import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FiPlus, FiDollarSign, FiPackage, FiArrowLeft } from 'react-icons/fi';
import { getPrices } from '../../../../store/price.js';
import { getUserSubs, clearErrors, setRecentCheckout } from '../../../../store/user.js';
import ServiceCard from './ServiceCard';
import AddServiceDrawer from './AddServiceDrawer';
import ConfirmationDialog from './ConfirmationDialog';
import { getMonthlyCost } from '../utils/monthlyCost';

const ClientServiceList = () => {
	const dispatch = useDispatch();
	const history = useHistory();

	// Redux state
	const token = useSelector((st) => st.user.token);
	const currentUser = useSelector((st) => st.user.currentUser);
	const prices = useSelector((st) => st.price.allPrices);
	const userSubs = useSelector((st) => st.user.stripeSubs) || [];

	// Local state
	const [loading, setLoading] = useState(true);
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [message, setMessage] = useState('');
	const [dialogOpen, setDialogOpen] = useState(false);

	// Fetch data on mount
	useEffect(() => {
		const fetchData = async () => {
			if (!currentUser?.customerId || !token) {
				setLoading(false);
				return;
			}

			try {
				await Promise.all([
					dispatch(getUserSubs({ customerId: currentUser.customerId, token })),
					dispatch(getPrices({ token }))
				]);
			} catch (error) {
				console.error('Error fetching services:', error);
			} finally {
				setLoading(false);
			}
		};

		fetchData();

		// Check for redirect from checkout
		const query = new URLSearchParams(window.location.search);
		if (query.has('success')) {
			setMessage('Order placed! You will receive an email confirmation.');
			setDialogOpen(true);
			dispatch(setRecentCheckout(true));
			history.replace('/my-services');
		}
		if (query.has('canceled')) {
			setMessage("Order canceled -- continue to shop around and checkout when you're ready.");
			setDialogOpen(true);
		}

		return () => {
			dispatch(clearErrors());
		};
	}, [currentUser, token, dispatch]);

	// Helper: Sort prices highest to lowest
	const sortedPrices = prices
		? [...prices].sort((a, b) => b.unit_amount - a.unit_amount)
		: [];

	// Helper: Filter out already subscribed services
	const filteredPrices = sortedPrices.filter((p) => {
		if (!p.active || !p.product?.active) return false;
		return !userSubs.some(sub =>
			sub.items?.data?.[0]?.price?.product === p.product.id
		);
	});

	// Helper: Find price object for subscription
	const findPriceObject = (subObject) => {
		return prices?.find(p =>
			p.id === subObject.items?.data?.[0]?.price?.id
		);
	};

	// Calculate monthly cost
	const monthlyCost = getMonthlyCost(userSubs, prices);

	// Handlers
	const handleDialogClose = () => {
		setDialogOpen(false);
		setMessage('');
		history.push('/my-services');
	};

	const toggleDrawer = (open) => () => {
		setDrawerOpen(open);
	};

	// Loading state
	if (loading) {
		return (
			<div className='flex items-center justify-center min-h-screen bg-gray-50'>
				<div className='text-center'>
					<div className='w-16 h-16 border-4 border-dashed rounded-full animate-spin border-pink-500'></div>
					<h2 className='mt-4 font-glacial text-xl text-gray-700'>
						Loading Services...
					</h2>
				</div>
			</div>
		);
	}

	return (
		<div className='min-h-screen bg-gray-50'>
			{/* Header */}
			<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
				{/* Back Button */}
				<button
					onClick={() => history.push('/home')}
					className='inline-flex items-center text-sm font-montserrat-medium text-gray-600 hover:text-gray-900 transition-colors mb-6'
				>
					<FiArrowLeft className='mr-2 h-4 w-4' />
					Back to Home
				</button>

				{/* Page Header */}
				<div className='mb-8'>
					<div className='flex items-start justify-between gap-4'>
						<div className='flex-1'>
							<h1 className='text-3xl font-glacial font-bold text-gray-900 tracking-tight'>
								My Services
							</h1>
							<p className='mt-2 text-sm font-montserrat-regular text-gray-600'>
								{userSubs.length > 0
									? `You have ${userSubs.length} active ${userSubs.length === 1 ? 'subscription' : 'subscriptions'} totaling $${monthlyCost.toFixed(2)}/month`
									: 'Manage your active subscriptions and add new services.'
								}
							</p>
						</div>

						<button
							onClick={toggleDrawer(true)}
							className='flex items-center space-x-2 bg-[#CC1DB9] text-white px-4 py-2 rounded-md hover:bg-pink-600 transition-colors font-montserrat-medium text-sm whitespace-nowrap'
						>
							<FiPlus className='h-5 w-5' />
							<span className='hidden sm:inline'>Add Service</span>
							<span className='sm:hidden'>Add</span>
						</button>
					</div>
				</div>
			</div>

			{/* Services Grid */}
			<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
				{userSubs.length > 0 ? (
					<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'>
						{userSubs.map((sub) => {
							const price = findPriceObject(sub);
							return (
								<ServiceCard
									key={sub.id}
									subscription={sub}
									price={price}
									setLoading={setLoading}
								/>
							);
						})}
					</div>
				) : (
					<div className='bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center'>
						<div className='max-w-md mx-auto'>
							<div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4'>
								<FiPackage className='h-8 w-8 text-gray-400' />
							</div>
							<h3 className='text-xl font-glacial text-gray-900 mb-2'>
								No Services Yet
							</h3>
							<p className='text-gray-600 font-montserrat-regular mb-6'>
								Get started by adding your first service subscription.
							</p>
							<button
								onClick={toggleDrawer(true)}
								className='bg-[#CC1DB9] text-white px-6 py-3 rounded-md hover:bg-pink-600 transition-colors font-montserrat-medium'
							>
								Add Your First Service
							</button>
						</div>
					</div>
				)}
			</div>

			{/* Add Service Drawer */}
			<AddServiceDrawer
				open={drawerOpen}
				onClose={toggleDrawer(false)}
				availablePrices={filteredPrices}
				setLoading={setLoading}
			/>

			{/* Confirmation Dialog */}
			<ConfirmationDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				message={message}
				cancelText="OK"
			/>
		</div>
	);
};

export default ClientServiceList;